import { createAsyncThunk } from '@reduxjs/toolkit';
import { sowAdminStoreKey } from './sow-admin.const';
import axios, { AxiosResponse } from 'axios';
import { apiEndpoints } from '../../constants/api';
import {
  GetAccountsAdminResponse,
  PutResourceDto,
  EditResourceDto,
  DeactivateResourceDto,
  DeactivateMultipleResourceDto,
  AccountStatus,
} from '../../types';
import { notificationAlert } from '../notifications';
import { formatErrorMessage } from '../../utils';
import {
  ChReqStatus,
  ExecuteSowChangeReqRespDto,
  SowChangeReqDto,
  SowChangeReqForApprovalRespDto,
} from '../../types/sow-change-req';

export const fetchSowAdminAccounts = createAsyncThunk(
  `${sowAdminStoreKey}/fetchAccounts`,
  async ({
    date,
    pdmId,
    dmId,
    status,
    isPortfolio,
    accountId,
  }: {
    date: string | null;
    pdmId?: string[];
    dmId?: string[];
    status?: AccountStatus | null;
    isPortfolio?: boolean;
    accountId?: string;
  }) => {
    try {
      const response: AxiosResponse<GetAccountsAdminResponse> =
        await axios.post(apiEndpoints.fetchSowAdminAccounts(), {
          date,
          accountId,
          pdmId,
          dmId,
          status,
        });

      return { accounts: response.data.accounts, isPortfolio };
    } catch (err) {
      throw new Error(String(err));
    }
  },
);

export const requestMove = createAsyncThunk(
  `${sowAdminStoreKey}/requestMove`,
  async (resource: PutResourceDto, thunkAPI) => {
    try {
      const formData = new FormData();

      Object.entries(resource).forEach(([key, value]) => {
        if (key === 'files' && Array.isArray(value)) {
          value.forEach((file) => {
            formData.append('files', file);
          });
        } else if (value !== undefined && value !== null) {
          formData.append(
            key,
            typeof value === 'object' ? JSON.stringify(value) : String(value),
          );
        }
      });

      const response: AxiosResponse<
        SowChangeReqDto | ExecuteSowChangeReqRespDto
      > = await axios.post(apiEndpoints.resourceRequestMove(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      thunkAPI.dispatch(notificationAlert('Request was successfull'));

      return response.data;
    } catch (err) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(String(err));
    }
  },
);

export const requestEdit = createAsyncThunk(
  `${sowAdminStoreKey}/requestEdit`,
  async (resource: EditResourceDto, thunkAPI) => {
    try {
      const formData = new FormData();

      Object.entries(resource).forEach(([key, value]) => {
        if (key === 'files' && Array.isArray(value)) {
          value.forEach((file) => {
            formData.append('files', file);
          });
        } else if (value !== undefined && value !== null) {
          formData.append(
            key,
            typeof value === 'object' ? JSON.stringify(value) : String(value),
          );
        }
      });

      const response: AxiosResponse<
        SowChangeReqDto | ExecuteSowChangeReqRespDto
      > = await axios.post(apiEndpoints.resourceRequestEdit(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      thunkAPI.dispatch(notificationAlert('Edit request was successfull'));

      return response.data;
    } catch (err) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(String(err));
    }
  },
);

export const requestDeactivate = createAsyncThunk(
  `${sowAdminStoreKey}/deactivate`,
  async (resource: DeactivateResourceDto, thunkAPI) => {
    try {
      const formData = new FormData();
      Object.entries(resource).forEach(([key, value]) => {
        if (key === 'files' && Array.isArray(value)) {
          value.forEach((file) => {
            formData.append('files', file);
          });
        } else if (value !== undefined && value !== null) {
          formData.append(
            key,
            typeof value === 'object' ? JSON.stringify(value) : String(value),
          );
        }
      });

      const response: AxiosResponse<
        SowChangeReqDto | ExecuteSowChangeReqRespDto
      > = await axios.post(apiEndpoints.resourceRequestDeactivate(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      thunkAPI.dispatch(
        notificationAlert('Deactivate request was successfull'),
      );
      return response.data;
    } catch (error) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(error), { variant: 'error' }),
      );
      throw new Error(String(error));
    }
  },
);

export const requestBulkDeactivate = createAsyncThunk(
  `${sowAdminStoreKey}/deactivate-multiple`,
  async (resource: DeactivateMultipleResourceDto, thunkAPI) => {
    try {
      const formData = new FormData();
      Object.entries(resource).forEach(([key, value]) => {
        if (key === 'files' && Array.isArray(value)) {
          value.forEach((file) => {
            formData.append('files', file);
          });
        } else if (key === 'resourceIds' && Array.isArray(value)) {
          value.forEach((id) => {
            formData.append('resourceIds', id);
          });
        } else if (value !== undefined && value !== null) {
          formData.append(
            key,
            typeof value === 'object' ? JSON.stringify(value) : String(value),
          );
        }
      });

      const response: AxiosResponse<
        SowChangeReqDto | ExecuteSowChangeReqRespDto
      > = await axios.post(
        apiEndpoints.resourceRequestDeactivateMultiple(),
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );

      thunkAPI.dispatch(notificationAlert('Deactivate request was successful'));
      return response.data;
    } catch (error) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(error), { variant: 'error' }),
      );
      throw new Error(String(error));
    }
  },
);

export const fetchSowChangeReqs = createAsyncThunk(
  `${sowAdminStoreKey}/fetchSowChangeReqs`,
  async ({}: any) => {
    try {
      const response: AxiosResponse<SowChangeReqForApprovalRespDto> =
        await axios.post(apiEndpoints.fetchSowChangeReqs(), {
          reqStatus: ChReqStatus.PENDING,
          orderBy: [
            {
              field: 'createdAt',
              direction: 'DESC',
            },
          ],
        });

      return response.data;
    } catch (err) {
      // thunkAPI.dispatch(
      //   notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      // );
      throw new Error(String(err));
    }
  },
);

export const executeSowChangeReq = createAsyncThunk(
  `${sowAdminStoreKey}/executeSowChangeReq`,
  async (payload: { id: string }, thunkAPI) => {
    try {
      const response: AxiosResponse<ExecuteSowChangeReqRespDto> =
        await axios.post(apiEndpoints.executeSowChangeReq(), payload);

      thunkAPI.dispatch(notificationAlert('Execute was successfull'));

      return response.data;
    } catch (err) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(String(err));
    }
  },
);

export const rejectSowChangeReq = createAsyncThunk(
  `${sowAdminStoreKey}/rejectSowChangeReq`,
  async (payload: { id: string }, thunkAPI) => {
    try {
      const response: AxiosResponse<void> = await axios.post(
        apiEndpoints.rejectSowChangeReq(),
        payload,
      );

      thunkAPI.dispatch(notificationAlert('Reject was successfull'));

      return response.data;
    } catch (err) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(String(err));
    }
  },
);
